import React, { useState, useCallback, useEffect } from 'react';
import PortfolioInput from './components/PortfolioInput';
import SpendingInput from './components/SpendingInput';
import WithdrawalInput from './components/WithdrawalInput';
import IncomeInput from './components/IncomeInput';
import PortfolioChart from './components/PortfolioChart';
import TotalPortfolioChart from './components/TotalPortfolioChart';
import WithdrawalChart from './components/WithdrawalChart';
import { MonteCarloSimulation } from './utils/MonteCarloSimulation';
import { loadScenarioData, ScenarioDataMap } from './data/ScenarioData';
import { AccountBalance, SimulationParameters, SimulationResults } from './types/AssetClasses';
import './App.css';

function App() {
  const [accountBalances, setAccountBalances] = useState<AccountBalance[]>([]);
  const [monthlySpendingTarget, setMonthlySpendingTarget] = useState<number>(0);
  const [socialSecurityMonthlyBenefit, setSocialSecurityMonthlyBenefit] = useState<number>(0);
  const [pensionMonthlyBenefit, setPensionMonthlyBenefit] = useState<number>(0);
  const [simulationPeriods, setSimulationPeriods] = useState<number>(35);
  const [numberOfSimulations, setNumberOfSimulations] = useState<number>(5000);
  const [simulationResults, setSimulationResults] = useState<SimulationResults | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [simulationDuration, setSimulationDuration] = useState<number | null>(null);
  const [scenarioData, setScenarioData] = useState<ScenarioDataMap | null>(null);
  const [scenarioError, setScenarioError] = useState<string>('');

  // Load scenario data in the background on mount
  useEffect(() => {
    let isMounted = true;
    loadScenarioData()
      .then(data => {
        if (isMounted) {
          setScenarioData(data);
        }
      })
      .catch(err => {
        if (isMounted) {
          setScenarioError('Failed to load scenario data: ' + (err as Error).message);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const handleAccountBalancesChange = useCallback((newAccountBalances: AccountBalance[]) => {
    setAccountBalances(newAccountBalances);
  }, []);

  const handleSpendingTargetChange = useCallback((withdrawal: number) => {
    setMonthlySpendingTarget(withdrawal);
  }, []);

  const handlePeriodsChange = useCallback((periods: number) => {
    setSimulationPeriods(periods);
  }, []);

  const handleSimulationsChange = useCallback((simulations: number) => {
    setNumberOfSimulations(simulations);
  }, []);

  const handleSocialSecurityChange = useCallback((socialSecurity: number) => {
    setSocialSecurityMonthlyBenefit(socialSecurity);
  }, []);

  const handlePensionChange = useCallback((pension: number) => {
    setPensionMonthlyBenefit(pension);
  }, []);

  const runSimulation = async () => {
    const totalValue = accountBalances.reduce((sum, account) => sum + account.balance, 0);
    
    if (totalValue <= 0) {
      setError('Please enter valid account balances');
      return;
    }

    if (!MonteCarloSimulation.validateAccountBalances(accountBalances)) {
      setError('Each account\'s allocations must sum to 100%');
      return;
    }

    if (monthlySpendingTarget < 0) {
      setError('Annual withdrawal cannot be negative');
      return;
    }

    if (!scenarioData) {
      setError('Scenario data not loaded yet. Please wait.');
      return;
    }

    setError('');
    setIsLoading(true);
    setSimulationDuration(null);

    try {
      const parameters: SimulationParameters = {
        accountBalances,
        socialSecurityMonthlyBenefit,
        monthlySpendingTarget: monthlySpendingTarget,
        simulationPeriods,
        numberOfSimulations
      };

      const startTime = performance.now();
      const simulation = new MonteCarloSimulation(parameters, { scenarioData });
      const results = simulation.runSimulation();
      const endTime = performance.now();
      
      setSimulationResults(results);
      setSimulationDuration(endTime - startTime);
    } catch (err) {
      setError('Error running simulation: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const canRunSimulation = () => {
    const totalValue = accountBalances.reduce((sum, account) => sum + account.balance, 0);
    return totalValue > 0 && 
           MonteCarloSimulation.validateAccountBalances(accountBalances) && 
           monthlySpendingTarget >= 0 &&
           simulationPeriods > 0 &&
           numberOfSimulations > 0 &&
           !!scenarioData;
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Monte Carlo Projection</h1>
        <p>Proof-of-concept to measure the performance of running the projection calculations in a Web Browser.</p>
      </header>

      <div className="notes-alert">
        <h3>NOTES</h3>
        <p>This model differs from the provided DRAFT spreadsheet model in <em>at least</em> the following ways:</p>
        <ul>
          <li><strong>Annual calculations</strong> - All calculations are based on annual amounts for inflation, returns, withdrawals, etc. instead of monthly.</li>
          <li><strong>Annual rebalancing</strong> - Each account type's assets are explicitly rebalanced annually to maintain the original allocation percentages. (It wasn't obvious if this happens in the spreadsheet.)</li>
          <li><strong>First year differences</strong> - The first simulated year is the end of one year following the beginning of the projection. The difference between calendar years and the starting point of the projection is ignored.</li>
          <li><strong>Taxes are not calculated, estimated, or included</strong> - All income and withdrawals are treated as tax-free</li>
          <li><strong>No RMDs</strong> - Required minimum distributions are not factored into the simulation</li>
          <li><strong>Annuities are not modeled</strong> - No annuity products or are modeled</li>
          <li><strong>Pension payments are not modeled</strong> - Pension income is not factored into the simulation</li>
        </ul>
      </div>

      <main className="App-main">
        <div className="input-section">
          <PortfolioInput
            onAccountBalancesChange={handleAccountBalancesChange}
          />
        </div>
        <div className="input-section">
          <IncomeInput
            pensionMonthlyBenefit={pensionMonthlyBenefit}
            onSocialSecurityChange={handleSocialSecurityChange}
            onPensionChange={handlePensionChange}
          />
        </div>

        <div className="input-section">
          <SpendingInput
            onSpendingTargetChanged={handleSpendingTargetChange}
          />
        </div>

        <div className="input-section">
          <WithdrawalInput
            simulationPeriods={simulationPeriods}
            numberOfSimulations={numberOfSimulations}
            onPeriodsChange={handlePeriodsChange}
            onSimulationsChange={handleSimulationsChange}
          />
        </div>

        <div className="simulation-section">
          <button 
            className="run-simulation-btn"
            onClick={runSimulation}
            disabled={!canRunSimulation() || isLoading}
          >
            {isLoading ? 'Running Simulation...' : 'Run Monte Carlo Simulation'}
          </button>
          
          {simulationDuration !== null && (
            <div className="simulation-duration">
              {simulationResults?.numberOfSimulations.toLocaleString()} simulations of {simulationResults?.simulationPeriods.toLocaleString()} years completed in {(simulationDuration / 1000).toFixed(3)} seconds
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
          {scenarioError && <div className="error-message">{scenarioError}</div>}
          {!scenarioData && !scenarioError && (
            <div className="loading-message">Loading scenario data…</div>
          )}
        </div>

        {simulationResults && (
          <>
            <div className="results-section">
              <TotalPortfolioChart data={simulationResults.totalPortfolioData} isLoading={isLoading} />
            </div>

            <div className="results-section">
              <PortfolioChart 
                data={simulationResults.accountBreakdown} 
                allSimulationRuns={simulationResults.allSimulationRuns}
                isLoading={isLoading} 
              />
            </div>

            <div className="results-section">
              <WithdrawalChart data={simulationResults.withdrawalData} isLoading={isLoading} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
